import { useState } from "react"
import Enums from "../../utilities/enum"
import { DateRangePicker } from "../shared/dateRangePicker"
import { SearchSelect } from "../shared/searchSelect"
import { SearchFiltersModal } from "../shared/searchFiltersModal"
import stringHelpers from "../../utilities/stringHelpers"
import { toDateString } from "../../utilities/dateUtilities"
import { TagSelector } from "../shared/tagSelector"
import appliedFiltersUtilities from "../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../utilities/arrayUtilities"

export const DashboardFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialStartDate,
    initialEndDate,
    initialType,
    initialStatus,
    initialSiteId,
    initialSelectedTags = [],
    initialAppliedFilters,
    showStatusFilter = true,
    sites = [],
    tags = []
}) => {
    const [startDate, setStartDate] = useState(initialStartDate)
    const [endDate, setEndDate] = useState(initialEndDate)
    const [type, setType] = useState(initialType)
    const [status, setStatus] = useState(initialStatus)
    const [siteId, setSiteId] = useState(initialSiteId)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const clearLocalFilters = () => {
        setStartDate(null)
        setEndDate(null)
        setType(null)
        setSiteId(null)
        setStatus(0)
        setSelectedTags([])
        setAppliedFilters(showStatusFilter ? { "Status": Enums.BreachStatus[0] } : null)
    }

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    return <SearchFiltersModal
        title="Filter Results"
        handleClose={ handleClose }
        handleConfirm={ () => handleConfirm({ startDate, endDate, siteId, type, status, selectedTags, appliedFilters }) }
        showModal={ showModal }
        key="main-dashboard-filters-modal"
        handleReset={ clearLocalFilters }
    >
        <div className="w-100">
            <div className="mb-2">
                <label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
                <DateRangePicker
                    fromDate={ startDate }
                    toDate={ endDate }
                    setDateRange={ (firstDate, lastDate, presetLabel) => {
                        const isFirstDateEmpty = firstDate === null || firstDate === ""
                        const isLastDateEmpty = lastDate === null || lastDate === ""
                        setStartDate(isFirstDateEmpty ? null : firstDate);
                        setEndDate(isLastDateEmpty ? null : lastDate);
                        if (!stringHelpers.isNullOrEmpty(presetLabel)) {
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                delete updatedFilters.From
                                delete updatedFilters.To
                                updatedFilters["Date Range"] = presetLabel
                                return updatedFilters
                            })
                        }
                        else {
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                if (!isFirstDateEmpty && !isLastDateEmpty) {
                                    delete updatedFilters.From
                                    delete updatedFilters.To
                                    updatedFilters["Date Range"] = `${ toDateString(new Date(firstDate)) } - ${ toDateString(new Date(lastDate)) }`
                                } else {
                                    if (!isFirstDateEmpty) {
                                        updatedFilters.From = toDateString(new Date(firstDate))
                                    } else {
                                        delete updatedFilters.From
                                    }
                                    if (!isLastDateEmpty) {
                                        updatedFilters.To = toDateString(new Date(lastDate))
                                    } else {
                                        delete updatedFilters.To
                                    }
                                    delete updatedFilters["Date Range"]
                                }
                                return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                            })
                        }
                    } }
                    showClearOption
                    hideLabel
                    fullWidth
                    dropdownPositionRight
                />
            </div>

            <div className="d-flex flex-column mb-2">
                <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                <SearchSelect
                    defaultLabel="All"
                    width="100%"
                    options={ [{ value: "", label: "All" }, ...sites.map(item => ({ value: item.id, label: item.name }))] }
                    onSelect={ value => {
                        if (!stringHelpers.isNullOrEmpty(value)) {
                            setSiteId(value)
                            const selectedSite = sites.find(item => item.id === value)
                            setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite.name }))
                        } else {
                            setSiteId(null)
                            setAppliedFilters(prevState => {
                                const updatedFilters = { ...prevState }
                                delete updatedFilters["Site"]
                                return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                            })
                        }
                    } }
                    selected={ !stringHelpers.isNullOrEmpty(siteId) ? siteId : null }
                />
            </div>

            { !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                <div className="d-flex align-items-center justify-content-between">
                    <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                    <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                </div>
                <TagSelector
                    onChange={ handleTagsChange }
                    selectedTags={ selectedTags }
                    tags={ tags }
                />
            </div> }

            <div className="d-flex flex-column mb-2">
                <label className="m-0 mr-2 mb-1 nowrap">Type</label>
                <select
                    value={ type === null ? "" : type }
                    onChange={ e => {
                        const value = stringHelpers.isNullOrEmpty(e.target.value) ? null : Number(e.target.value)
                        setType(value)
                        setAppliedFilters(prevState => {
                            const updatedFilters = { ...prevState }
                            if (value === null) {
                                delete updatedFilters["Type"]
                            } else {
                                updatedFilters["Type"] = Enums.ContraventionTypes[value]
                            }
                            return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                        })
                    } }
                    className="border rounded px-2 py-1 w-100"
                >
                    <option value="">All</option>
                    {
                        Object
                            .entries(Enums.ContraventionTypes)
                            .map(([key, value]) => <option value={ key }>{ value }</option>)
                    }
                </select>
            </div>

            {
                showStatusFilter &&
                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Status</label>
                    <select
                        value={ status === null ? "" : status }
                        onChange={ e => {
                            const value = stringHelpers.isNullOrEmpty(String(e.target.value)) ? null : Number(e.target.value)
                            setStatus(value)
                            setAppliedFilters(prevState => ({
                                ...prevState,
                                "Status": value === null ? "All" : Enums.BreachStatus[value]
                            }))
                        } }
                        className="border rounded px-2 py-1 w-100"
                    >
                        <option value="">All</option>
                        <option value={ 0 }>New</option>
                        <option value={ 503 }>Void</option>
                    </select>
                </div>
            }
        </div>
    </SearchFiltersModal>
}
